import { useSearchParams } from 'react-router-dom';
import { Spinner } from '@klaro7/ui';

import { useCustomerDeposits, useBonusCosts } from '@/features/revenue/api';

/** Mese corrente in formato YYYY-MM. */
function currentMonth(): string {
  return new Date().toLocaleDateString('en-CA').slice(0, 7);
}

function monthsAgo(n: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Oggi in formato YYYY-MM-DD (ora locale del browser). */
function today(): string {
  return new Date().toLocaleDateString('en-CA');
}

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const DAY_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** Ultimo giorno del mese YYYY-MM, senza superare oggi. */
function lastDayOfMonth(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
  return last > today() ? today() : last;
}

/** "2026-09-10" -> "10/09/2026"; "2026-09" -> "set 2026". */
function periodLabel(v: string): string {
  if (DAY_RE.test(v)) {
    const [y, m, d] = v.split('-');
    return `${d}/${m}/${y}`;
  }
  return monthLabel(v);
}

function euro(n: number): string {
  return `€ ${n.toFixed(2)}`;
}

/** "2026-07" -> "lug 2026" */
function monthLabel(period: string): string {
  const [year, month] = period.split('-').map(Number) as [number, number];
  const label = new Date(year, month - 1, 1).toLocaleDateString('it-IT', {
    month: 'short',
    year: 'numeric',
  });
  return label;
}

function PeriodPicker({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="text-sm text-neutral-600">
        <span className="mb-1 block font-medium text-neutral-700">Dal mese</span>
        <input
          type="month"
          value={from}
          max={to}
          onChange={(e) => onChange(e.target.value, to)}
          className="h-10 rounded-lg border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal/50 focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
        />
      </label>
      <label className="text-sm text-neutral-600">
        <span className="mb-1 block font-medium text-neutral-700">Al mese</span>
        <input
          type="month"
          value={to}
          min={from}
          max={currentMonth()}
          onChange={(e) => onChange(from, e.target.value)}
          className="h-10 rounded-lg border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal/50 focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
        />
      </label>
    </div>
  );
}

/** Versato da ogni cliente, un mese per colonna. */
function CustomerDepositsCard({ from, to }: { from: string; to: string }) {
  const { data, isLoading, error } = useCustomerDeposits(from, to);

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-neutral-900">Versamenti per cliente</h2>
      <p className="mt-1 text-sm text-neutral-500">
        Denaro realmente versato, mese per mese. I bonus non sono inclusi.
      </p>

      {isLoading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}
      {error && (
        <p className="mt-4 text-sm text-red-600">Impossibile caricare il report.</p>
      )}

      {data && data.rows.length === 0 && (
        <p className="mt-6 text-sm text-neutral-500">Nessuna ricarica nel periodo scelto.</p>
      )}

      {data && data.rows.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-neutral-600">
                <th className="py-2 pr-4 font-medium">Cliente</th>
                {data.months.map((m) => (
                  <th key={m} className="py-2 px-3 text-right font-medium">
                    {monthLabel(m)}
                  </th>
                ))}
                <th className="py-2 pl-3 text-right font-medium">Totale</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.userId} className="border-b border-neutral-100">
                  <td className="py-2 pr-4">
                    <div className="font-medium text-neutral-900">{row.nome}</div>
                    <div className="text-xs text-neutral-500">{row.email}</div>
                  </td>
                  {row.perMese.map((amount, i) => (
                    <td
                      key={data.months[i]}
                      className={`py-2 px-3 text-right tabular-nums ${
                        amount === 0 ? 'text-neutral-300' : 'text-neutral-900'
                      }`}
                    >
                      {amount === 0 ? '—' : euro(amount)}
                    </td>
                  ))}
                  <td className="py-2 pl-3 text-right font-semibold tabular-nums text-neutral-900">
                    {euro(row.totale)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold text-neutral-900">
                <td className="py-2 pr-4">Totale</td>
                {data.totaliPerMese.map((amount, i) => (
                  <td key={data.months[i]} className="py-2 px-3 text-right tabular-nums">
                    {euro(amount)}
                  </td>
                ))}
                <td className="py-2 pl-3 text-right tabular-nums">{euro(data.totaleGenerale)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

const inputCls =
  'h-10 rounded-lg border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal/50 focus:outline-none focus:ring-2 focus:ring-brand-teal/40';

/**
 * Filtro del report bonus: a mesi o a giorni (estremi inclusi, confini a
 * mezzanotte italiana lato server). Senza filtro proprio segue il periodo
 * generale della pagina; "Azzera" ci torna.
 */
function BonusPeriodFilter({
  from,
  to,
  isCustom,
  onChange,
  onReset,
}: {
  from: string;
  to: string;
  isCustom: boolean;
  onChange: (from: string, to: string) => void;
  onReset: () => void;
}) {
  const byDay = DAY_RE.test(from) || DAY_RE.test(to);
  const setMode = (day: boolean) => {
    if (day === byDay) return;
    if (day) onChange(`${from.slice(0, 7)}-01`, lastDayOfMonth(to.slice(0, 7)));
    else onChange(from.slice(0, 7), to.slice(0, 7));
  };
  const modeBtn = (active: boolean) =>
    `px-3 py-1.5 text-sm font-medium transition-colors ${
      active ? 'bg-brand-teal text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50'
    }`;

  return (
    <div className="mt-4 flex flex-wrap items-end gap-3">
      <div role="group" aria-label="Granularità" className="flex overflow-hidden rounded-lg border border-neutral-300">
        <button type="button" className={modeBtn(!byDay)} aria-pressed={!byDay} onClick={() => setMode(false)}>
          Mese
        </button>
        <button type="button" className={modeBtn(byDay)} aria-pressed={byDay} onClick={() => setMode(true)}>
          Giorno
        </button>
      </div>
      <label className="text-sm text-neutral-600">
        <span className="mb-1 block font-medium text-neutral-700">{byDay ? 'Dal giorno' : 'Dal mese'}</span>
        <input
          type={byDay ? 'date' : 'month'}
          value={from}
          max={to}
          onChange={(e) => e.target.value && onChange(e.target.value, to)}
          className={inputCls}
        />
      </label>
      <label className="text-sm text-neutral-600">
        <span className="mb-1 block font-medium text-neutral-700">{byDay ? 'Al giorno' : 'Al mese'}</span>
        <input
          type={byDay ? 'date' : 'month'}
          value={to}
          min={from}
          max={byDay ? today() : currentMonth()}
          onChange={(e) => e.target.value && onChange(from, e.target.value)}
          className={inputCls}
        />
      </label>
      <button
        type="button"
        onClick={onReset}
        disabled={!isCustom}
        className="h-10 rounded-lg border border-neutral-300 bg-white px-4 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Azzera
      </button>
    </div>
  );
}

/** Costo dei bonus per tipo: erogato, consumato davvero, stornato. */
function BonusCostsCard({
  from,
  to,
  isCustom,
  onChange,
  onReset,
}: {
  from: string;
  to: string;
  isCustom: boolean;
  onChange: (from: string, to: string) => void;
  onReset: () => void;
}) {
  // Confronto sul giorno iniziale/finale effettivo (i mesi valgono dal 1° all'ultimo).
  const startDay = MONTH_RE.test(from) ? `${from}-01` : from;
  const endDay = MONTH_RE.test(to) ? `${to}-31` : to;
  const validRange = startDay <= endDay;
  const { data, isLoading, error } = useBonusCosts(from, to, validRange);

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-neutral-900">Costo dei bonus per tipo</h2>
      <p className="mt-1 text-sm text-neutral-500">
        La colonna <strong>Consumato</strong> è il costo reale: bonus effettivamente spesi in
        chiamata. Lo <strong>Scaduto</strong> non è mai uscito dalla piattaforma.
      </p>

      <BonusPeriodFilter from={from} to={to} isCustom={isCustom} onChange={onChange} onReset={onReset} />
      <p className="mt-2 text-xs text-neutral-500">
        Bonus erogati dal <strong>{periodLabel(from)}</strong> al <strong>{periodLabel(to)}</strong>{' '}
        (estremi inclusi, ora italiana).
      </p>
      {!validRange && (
        <p className="mt-4 text-sm text-red-600">La data iniziale è successiva a quella finale.</p>
      )}

      {isLoading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}
      {error && <p className="mt-4 text-sm text-red-600">Impossibile caricare il report.</p>}

      {data && data.rows.length === 0 && (
        <p className="mt-6 text-sm text-neutral-500">Nessun bonus erogato nel periodo scelto.</p>
      )}

      {data && data.rows.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-neutral-600">
                <th className="py-2 pr-4 font-medium">Tipo di bonus</th>
                <th className="py-2 px-3 text-right font-medium">Blocchi</th>
                <th className="py-2 px-3 text-right font-medium">Erogato</th>
                <th className="py-2 px-3 text-right font-medium">Consumato</th>
                <th className="py-2 px-3 text-right font-medium">Scaduto</th>
                <th className="py-2 pl-3 text-right font-medium">Ancora attivo</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.source} className="border-b border-neutral-100">
                  <td className="py-2 pr-4 font-medium text-neutral-900">{row.label}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-neutral-600">{row.blocchi}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-neutral-600">
                    {euro(row.erogato)}
                  </td>
                  <td className="py-2 px-3 text-right font-semibold tabular-nums text-neutral-900">
                    {euro(row.consumato)}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums text-neutral-500">
                    {euro(row.scaduto)}
                  </td>
                  <td className="py-2 pl-3 text-right tabular-nums text-neutral-500">
                    {euro(row.residuoAttivo)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold text-neutral-900">
                <td className="py-2 pr-4">Totale</td>
                <td className="py-2 px-3 text-right tabular-nums">{data.totali.blocchi}</td>
                <td className="py-2 px-3 text-right tabular-nums">{euro(data.totali.erogato)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{euro(data.totali.consumato)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{euro(data.totali.scaduto)}</td>
                <td className="py-2 pl-3 text-right tabular-nums">
                  {euro(data.totali.residuoAttivo)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

export function AdminReportsPage() {
  // Periodi nell'URL: sopravvivono a refresh e al link condiviso.
  const [searchParams, setSearchParams] = useSearchParams();
  const qFrom = searchParams.get('from');
  const qTo = searchParams.get('to');
  const from = qFrom && MONTH_RE.test(qFrom) ? qFrom : monthsAgo(5);
  const to = qTo && MONTH_RE.test(qTo) ? qTo : currentMonth();
  const qbFrom = searchParams.get('bonusFrom') ?? '';
  const qbTo = searchParams.get('bonusTo') ?? '';
  const isPeriod = (v: string) => MONTH_RE.test(v) || DAY_RE.test(v);
  const bonusIsCustom = isPeriod(qbFrom) && isPeriod(qbTo);
  const bonusFrom = bonusIsCustom ? qbFrom : from;
  const bonusTo = bonusIsCustom ? qbTo : to;

  const patchParams = (patch: Record<string, string | null>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 p-6">
        <h1 className="text-2xl font-semibold text-white">Report amministrativi</h1>
        <p className="mt-1 text-sm text-white/70">
          Versamenti dei clienti mese su mese e costo dei bonus per tipo.
        </p>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <PeriodPicker
          from={from}
          to={to}
          onChange={(nextFrom, nextTo) => patchParams({ from: nextFrom || from, to: nextTo || to })}
        />
      </div>

      <CustomerDepositsCard from={from} to={to} />
      <BonusCostsCard
        from={bonusFrom}
        to={bonusTo}
        isCustom={bonusIsCustom}
        onChange={(f, t) => patchParams({ bonusFrom: f, bonusTo: t })}
        onReset={() => patchParams({ bonusFrom: null, bonusTo: null })}
      />
    </div>
  );
}
